/**
 * How a report is laid out: what the settings are, together.
 *
 * The individual options live next door, one file per kind of decision:
 * `page.ts` for measurements, `colour.ts` for colours. This file only composes
 * them, reads them back off a stored document, and turns them into the custom
 * properties the page draws itself from.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * **To add a setting**: add its table to `page.ts` or `colour.ts`, add a field
 * to `Layout` and a line to each of `DEFAULT_LAYOUT`, `readLayout` and
 * `layoutStyle` below, and give it a name in both message files. The compiler
 * names every one of those places if you miss it. `textStyles` is the one
 * exception: it is a list rather than a single value, so it has no line in
 * `layoutStyle` — see the field's own comment.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * **A measurement is stored as a measurement, not as the name of a preset.**
 * `pageWidth` holds `210mm`, not `a4`. That is what lets a measurement be
 * typed: the presets become shortcuts that write a value rather than the only
 * values there are, and a picker decides which preset to show as chosen by
 * comparing what is stored against each of them. Reading a document written
 * when these were names is handled in `readLayout`, and costs nothing: the
 * name fails to parse as a measurement, so the preset it used to mean stands
 * as the fallback.
 *
 * Every setting belongs to the **document**, not the reader, for the same
 * reason the typeface does: the person approving a report has to see what the
 * person who wrote it saw, and the PDF has to match both.
 */

import { BLEED_IDS, BLEED_RULE, BLEEDS, DEFAULT_BLEED } from '@/domain/layout/bleed'
import {
  CHART_COLOURS,
  DEFAULT_ACCENT,
  PAPER_IDS,
  PAPERS,
  readAccent,
  readableInk,
  readPalette,
  type ChartColours,
  type Paper,
} from '@/domain/layout/colour'
import {
  ASSURANCE_IDS,
  CURRENCY_IDS,
  ESG_FRAMEWORK_IDS,
  FIGURE_SCALE_IDS,
  FRAMEWORK_IDS,
  HEADING_NUMBER_IDS,
  type Assurance,
  type Currency,
  type EsgFramework,
  type FigureScale,
  type Framework,
  type HeadingNumber,
} from '@/domain/layout/dossier'
import { readMeasure } from '@/domain/layout/measure'
import {
  MARGIN_IDS,
  MARGIN_RULE,
  MARGINS,
  marginShorthand,
  oneOf,
  PAGE_RULE,
  PAGE_SIZE_IDS,
  PAGE_SIZES,
  TEXT_SIZE_IDS,
  TEXT_SIZE_RULE,
  TEXT_SIZES,
} from '@/domain/layout/page'
import {
  TABLE_DENSITIES,
  TABLE_DENSITY_IDS,
  TABLE_HEAD_IDS,
  TABLE_HEADS,
  TABLE_RULE_IDS,
  TABLE_RULES,
  TABLE_ZEBRA,
  type TableDensity,
  type TableHead,
  type TableRule,
} from '@/domain/layout/tables'
import { readTextStyles, type TextStyle } from '@/domain/layout/textStyles'
import { isTypefaceId, typefaceOf, type TypefaceId } from '@/domain/typeface'

export * from '@/domain/layout/background'
export * from '@/domain/layout/bleed'
export * from '@/domain/layout/colour'
export * from '@/domain/layout/measure'
export * from '@/domain/layout/dossier'
export * from '@/domain/layout/page'
export * from '@/domain/layout/tables'
export * from '@/domain/layout/textStyles'

/** Everything about a report's page, apart from its typeface. */
export interface Layout {
  /** The sheet, in millimetres. A preset paper, or one that was typed. */
  readonly pageWidth: string
  readonly pageHeight: string

  /**
   * The white space around the text, in millimetres.
   *
   * Three measurements rather than one, because they are three decisions: a
   * printed page wants more room at the foot than the head, since a text block
   * sitting slightly above centre looks placed rather than dropped. A preset
   * writes all three at once.
   */
  readonly marginTop: string
  readonly marginSide: string
  readonly marginBottom: string

  /** Body text, in points. */
  readonly textSize: string
  /**
   * Justified, as a printed report is set.
   *
   * Worth being a choice: justification needs hyphenation to avoid opening
   * rivers of white space, and text the browser cannot hyphenate reads better
   * ragged right.
   */
  readonly justified: boolean
  /**
   * The house colour, as `#rrggbb`.
   *
   * Spent on rules and, if asked, headings. Never on backgrounds: a financial
   * statement printed on a tinted panel is harder to read and photocopies
   * badly, and it is still photocopied.
   */
  readonly accent: string
  /** Whether headings take the accent colour, or stay in the text colour. */
  readonly accentHeadings: boolean
  readonly paper: Paper
  /**
   * The charter's other colours, beside the accent: `#rrggbb` each, at most
   * `MAX_PALETTE`. Offered for words in the text palette, and taken by charts
   * in turn where `chartColours` asks for them.
   */
  readonly palette: readonly string[]
  /** Where charts take their colours from: shades of the accent, or the charter's colours. */
  readonly chartColours: ChartColours
  /**
   * The typeface headings are set in, or `document` for the report's own.
   * A serif title over sans text, or the other way round, is the commonest
   * pairing a charter makes.
   */
  readonly headingTypeface: TypefaceId | 'document'

  /** How headings are numbered through the document, if at all. */
  readonly headingNumbers: HeadingNumber

  // ---------- Tables ----------
  /** How the head row is drawn, in every table that does not draw its own. */
  readonly tableHead: TableHead
  readonly tableRule: TableRule
  readonly tableDensity: TableDensity
  /** A faint band on every other row, for a table too wide to follow. */
  readonly tableZebra: boolean
  /**
   * Negatives in red.
   *
   * Off by default, and deliberately: a statement in black with negatives in
   * parentheses is the convention, and red does not survive a photocopier,
   * which is still how a great many of these are read.
   */
  readonly negativesInRed: boolean

  // ---------- What kind of dossier this is ----------
  /** The accounting framework, printed on the cover. */
  readonly framework: Framework
  /** The sustainability framework, where the report has one. */
  readonly esgFramework: EsgFramework
  /** What an auditor has said about it. */
  readonly assurance: Assurance
  readonly currency: Currency
  /** Units, thousands or millions, said above each table rather than applied. */
  readonly figureScale: FigureScale

  // ---------- The sheet itself ----------
  /**
   * How far a full-bleed element runs past the edge of the paper.
   *
   * Belongs to the document rather than to each picture: it is a property of
   * how this report will be printed, and a report with one picture bleeding
   * 3mm and another bleeding 5mm is a report nobody set up on purpose.
   */
  readonly bleed: string

  // ---------- Structure ----------
  /** Whether a chapter begins on a fresh page when printed. */
  readonly chapterPageBreak: boolean
  /** Leader dots between a contents entry and its page. */
  readonly contentsLeaders: boolean

  /**
   * Named colour-and-typeface combinations, ready to apply to a stretch of
   * prose from the text toolbar.
   *
   * Not a line in `layoutStyle` below like the rest of this interface: those
   * are each one custom property on the page, but this is a list of any
   * length, so it is turned into a whole stylesheet instead — see
   * `textStyleSheet` in `textStyles.ts`, and `useTextStyleSheet` for where
   * that stylesheet actually reaches the page.
   */
  readonly textStyles: readonly TextStyle[]
}

export const DEFAULT_LAYOUT: Layout = {
  // Taken from the preset tables rather than written again, so a default and
  // the preset it is named after cannot drift apart.
  pageWidth: PAGE_SIZES.a4.width,
  pageHeight: PAGE_SIZES.a4.height,

  marginTop: MARGINS.normal.top,
  marginSide: MARGINS.normal.side,
  marginBottom: MARGINS.normal.bottom,

  textSize: TEXT_SIZES.normal,
  justified: true,
  accent: DEFAULT_ACCENT,
  accentHeadings: false,
  paper: 'white',
  palette: [],
  chartColours: 'shades',
  headingTypeface: 'document',

  headingNumbers: 'none',

  tableHead: 'underline',
  tableRule: 'horizontal',
  tableDensity: 'normal',
  tableZebra: false,
  negativesInRed: false,

  framework: 'none',
  esgFramework: 'none',
  assurance: 'none',
  currency: 'eur',
  figureScale: 'none',

  bleed: BLEEDS[DEFAULT_BLEED],

  chapterPageBreak: false,
  contentsLeaders: true,

  textStyles: [],
}

/** A boolean, keeping a deliberate `false` rather than treating it as absent. */
function flag(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback
}

/** The headings' typeface: one the application knows, or the document's own. */
function readHeadingTypeface(value: unknown): TypefaceId | 'document' {
  return typeof value === 'string' && isTypefaceId(value) ? value : 'document'
}

/** What a stored layout can look like: anything, since it came off a server. */
export type StoredLayout = Readonly<Record<string, unknown>>

/**
 * A layout read from a stored document.
 *
 * Every field falls back on its own, so a report written before one of these
 * settings existed opens with the rest of its layout intact. Every report
 * already saved is such a document, which makes this the ordinary case rather
 * than the edge one.
 */
export function readLayout(source: StoredLayout): Layout {
  // What each setting used to be a name for. A document written before these
  // were measurements has that name stored where a measurement now goes; it
  // does not parse, so this is what stands, and the report opens looking
  // exactly as it did. A document written since has a measurement stored and
  // never reaches these.
  const paper = PAGE_SIZES[oneOf(PAGE_SIZE_IDS, source['pageSize'], 'a4')]
  const margin = MARGINS[oneOf(MARGIN_IDS, source['margin'], 'normal')]
  const text = TEXT_SIZES[oneOf(TEXT_SIZE_IDS, source['textSize'], 'normal')]
  const bleed = BLEEDS[oneOf(BLEED_IDS, source['bleed'], DEFAULT_BLEED)]

  return {
    pageWidth: readMeasure(source['pageWidth'] ?? source['pageSize'], PAGE_RULE, paper.width),
    pageHeight: readMeasure(source['pageHeight'] ?? source['pageSize'], PAGE_RULE, paper.height),

    marginTop: readMeasure(source['marginTop'] ?? source['margin'], MARGIN_RULE, margin.top),
    marginSide: readMeasure(source['marginSide'] ?? source['margin'], MARGIN_RULE, margin.side),
    marginBottom: readMeasure(
      source['marginBottom'] ?? source['margin'],
      MARGIN_RULE,
      margin.bottom,
    ),

    textSize: readMeasure(source['textSize'], TEXT_SIZE_RULE, text),

    justified: flag(source['justified'], DEFAULT_LAYOUT.justified),
    accent: readAccent(source['accent']),
    accentHeadings: flag(source['accentHeadings'], DEFAULT_LAYOUT.accentHeadings),
    paper: oneOf(PAPER_IDS, source['paper'], DEFAULT_LAYOUT.paper),
    palette: readPalette(source['palette']),
    chartColours: oneOf(CHART_COLOURS, source['chartColours'], DEFAULT_LAYOUT.chartColours),
    headingTypeface: readHeadingTypeface(source['headingTypeface']),

    headingNumbers: oneOf(
      HEADING_NUMBER_IDS,
      source['headingNumbers'],
      DEFAULT_LAYOUT.headingNumbers,
    ),

    tableHead: oneOf(TABLE_HEAD_IDS, source['tableHead'], DEFAULT_LAYOUT.tableHead),
    tableRule: oneOf(TABLE_RULE_IDS, source['tableRule'], DEFAULT_LAYOUT.tableRule),
    tableDensity: oneOf(TABLE_DENSITY_IDS, source['tableDensity'], DEFAULT_LAYOUT.tableDensity),
    tableZebra: flag(source['tableZebra'], DEFAULT_LAYOUT.tableZebra),
    negativesInRed: flag(source['negativesInRed'], DEFAULT_LAYOUT.negativesInRed),

    framework: oneOf(FRAMEWORK_IDS, source['framework'], DEFAULT_LAYOUT.framework),
    esgFramework: oneOf(ESG_FRAMEWORK_IDS, source['esgFramework'], DEFAULT_LAYOUT.esgFramework),
    assurance: oneOf(ASSURANCE_IDS, source['assurance'], DEFAULT_LAYOUT.assurance),
    currency: oneOf(CURRENCY_IDS, source['currency'], DEFAULT_LAYOUT.currency),
    figureScale: oneOf(FIGURE_SCALE_IDS, source['figureScale'], DEFAULT_LAYOUT.figureScale),

    bleed: readMeasure(source['bleed'], BLEED_RULE, bleed),

    chapterPageBreak: flag(source['chapterPageBreak'], DEFAULT_LAYOUT.chapterPageBreak),
    contentsLeaders: flag(source['contentsLeaders'], DEFAULT_LAYOUT.contentsLeaders),

    textStyles: readTextStyles(source['textStyles']),
  }
}

/**
 * The layout as CSS custom properties.
 *
 * One object the page is given wholesale, so the canvas, the preview and the
 * printed page are handed exactly the same measurements and cannot drift
 * apart. Every rule that already reads these variables follows without being
 * touched.
 */
export function layoutStyle(layout: Layout): Record<string, string> {
  const paper = PAPERS[layout.paper]

  // Read through `readAccent` rather than used as given: this reaches a
  // stylesheet, and it may have come from a document somebody else wrote.
  const accent = readAccent(layout.accent)

  const rule = TABLE_RULES[layout.tableRule]
  const density = TABLE_DENSITIES[layout.tableDensity]
  const head = TABLE_HEADS[layout.tableHead]

  // Each of the charter's colours under a number, for the words set in it.
  // Read again rather than trusted, as the accent is: these reach a stylesheet.
  const house = Object.fromEntries(
    readPalette(layout.palette).map((colour, at) => [`--house-${at + 1}`, colour]),
  )

  return {
    '--page-width': layout.pageWidth,
    '--page-height': layout.pageHeight,
    '--page-margin': marginShorthand(layout.marginTop, layout.marginSide, layout.marginBottom),
    /* Separately as well as together: a section that fills the page has to
       work out what the margins leave it, and cannot read a shorthand. */
    '--page-margin-top': layout.marginTop,
    '--page-margin-side': layout.marginSide,
    '--page-margin-bottom': layout.marginBottom,
    '--page-text-size': layout.textSize,
    '--page-align': layout.justified ? 'justify' : 'left',
    '--paper': paper.background,
    '--paper-ink': paper.ink,
    '--paper-rule': paper.rule,
    '--page-accent': accent,
    '--page-heading-ink': layout.accentHeadings ? accent : paper.ink,
    // Absent for the document's own, which the headings then fall back on.
    ...(layout.headingTypeface === 'document'
      ? {}
      : { '--font-heading': typefaceOf(layout.headingTypeface).stack }),
    ...house,

    '--table-head-wash': head.wash,
    '--table-head-line': head.line,
    '--table-head-line-ink': head.lineInk,
    '--table-head-text': layout.tableHead === 'filled' ? readableInk(accent) : 'currentColor',
    '--table-outer': rule.outer,
    '--table-inner': rule.inner,
    '--table-row': rule.row,
    '--table-pad': density.padding,
    '--table-leading': density.leading,
    '--table-zebra': layout.tableZebra ? TABLE_ZEBRA : 'transparent',
    '--table-negative': layout.negativesInRed ? '#b3261e' : 'inherit',

    '--page-bleed': layout.bleed,
  }
}

/**
 * The height a section filling the whole page has to reach.
 *
 * The paper less what the margins take. Worked out here rather than in a
 * stylesheet because it needs the two margins separately, and expressed as a
 * `calc` so it follows a change of paper or margin without anything
 * remeasuring.
 */
export function pageBodyHeight(layout: Layout): string {
  return `calc(${layout.pageHeight} - ${layout.marginTop} - ${layout.marginBottom})`
}
